import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowsLeftRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { fmtNum, timeAgo, formatAmountInput, parseAmountInput } from "../lib/format";

export function CurrencyConverter({ defaultTo = "IQD" }) {
  const { data: list } = useQuery({
    queryKey: ["currencies-all"],
    queryFn: async () => (await api.get("/currencies/search", { params: { q: "" } })).data.currencies,
  });
  const [from, setFrom] = useState(() => localStorage.getItem("mfc-conv-from") || "USD");
  const [to, setTo] = useState(() => localStorage.getItem("mfc-conv-to") || defaultTo);
  const [amount, setAmount] = useState("100");

  useEffect(() => { localStorage.setItem("mfc-conv-from", from); }, [from]);
  useEffect(() => { localStorage.setItem("mfc-conv-to", to); }, [to]);

  const { data: result, isFetching } = useQuery({
    queryKey: ["convert", from, to, amount],
    queryFn: async () => (await api.get("/convert", { params: { from, to, amount: parseFloat(amount) || 0 } })).data,
    enabled: !!amount && parseFloat(amount) >= 0,
  });

  const options = (list || []).map((c) => c.code);
  const swap = () => { setFrom(to); setTo(from); };

  const Select = ({ value, onChange, testid }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full h-11 border border-input bg-background px-3 text-sm font-mono">
      {options.includes(value) ? null : <option value={value}>{value}</option>}
      {options.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );

  return (
    <div className="border border-border bg-card p-5" data-testid="currency-converter">
      <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">Currency Converter</h3>
      <div className="space-y-3">
        <div className="space-y-2">
          <Label>Amount</Label>
          <Input type="text" inputMode="decimal" value={formatAmountInput(amount)} onChange={(e) => setAmount(parseAmountInput(e.target.value))} data-testid="convert-amount" className="font-mono h-11 rounded-none" placeholder="0" />
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
          <div className="space-y-2">
            <Label>From</Label>
            <Select value={from} onChange={setFrom} testid="convert-from" />
          </div>
          <Button type="button" variant="outline" onClick={swap} data-testid="convert-swap" title="Reverse currencies (e.g. USD→IQD becomes IQD→USD)" className="h-11 w-11 rounded-none shrink-0" aria-label="Reverse currencies">
            <ArrowsLeftRight size={18} />
          </Button>
          <div className="space-y-2">
            <Label>To</Label>
            <Select value={to} onChange={setTo} testid="convert-to" />
          </div>
        </div>
      </div>
      <div className="mt-5 border-t border-border pt-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Result</div>
        <div className="font-mono text-2xl font-bold tracking-tight" data-testid="convert-result">
          {result ? `${fmtNum(result.result)} ${to}` : (isFetching ? "…" : "—")}
        </div>
        {result && (
          <div className="text-xs text-muted-foreground font-mono mt-1">
            1 {from} = {fmtNum(result.rate)} {to} · Updated {timeAgo(result.updated_at)}
          </div>
        )}
      </div>
    </div>
  );
}
